/**
 * Het voorbeeld van een les, in een venster: links de tekst, rechts de
 * meerkeuzevragen met het goede antwoord gemarkeerd. De toets is gemaakt van
 * de tekst ernaast.
 *
 * Bovenaan kies je het niveau: elk niveau waarop een leerling in deze les
 * leest. Het voorbeeld opent op het niveau van de meeste leerlingen. De
 * leerkracht kan de toets op dat niveau opnieuw laten maken (leerlingen die
 * al aan de vragen begonnen, houden de hunne), en de les aan- of uitzetten.
 *
 * De tekst zelf bewerken of opnieuw laten maken kan de leerkracht sinds
 * stap 18 niet meer: de beheerder maakt en keurt de tekst op elk niveau
 * vooraf goed (tabblad Niveauteksten), met de lengte die bij het niveau
 * hoort.
 *
 * Bij historische fictie kan de leerkracht de bouwstenen tonen: de
 * markeringen [1A] ... [5B] in de tekst op dit niveau, zodat hij kan zien of
 * de bouwstenen er nog in staan (de leerling ziet ze nooit).
 *
 * Bij een groepsles (stap 23) staan rechts de groepjes in plaats van de
 * toets (onderdelen/Groepjes.js): er is geen toets.
 */
import React, { useEffect, useState } from 'react';
import { Check, RefreshCw, X } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { Venster } from '../../../components/Venster';
import { Draaier, Laden } from '../../../components/Laden';
import { zonderKopjes } from '../../../components/OpgemaakteTekst';
import { GemarkeerdeTekst, heeftMarkeringen } from '../../../components/Verhaalopzet';
import { TekstMaten } from '../../../components/TekstMaten';
import { Groepjes } from './Groepjes';

const kader = { maxHeight: '500px', overflow: 'auto', border: '1px solid var(--lijn-2)', padding: '16px', borderRadius: '4px' };

export function LesVoorbeeld({ les, onSluit, onAanUit, meldFout }) {
  const [niveaus, setNiveaus] = useState([]);
  const [niveau, setNiveau] = useState(null);
  const [tekst, setTekst] = useState(null);
  const [toets, setToets] = useState(null);
  const [laden, setLaden] = useState(true);       // bij het openen meteen "Preview laden..."
  const [opnieuw, setOpnieuw] = useState(false);
  const [melding, setMelding] = useState('');      // wat de server na opnieuw maken meldt
  const [bouwstenen, setBouwstenen] = useState(false);   // historische fictie: de markeringen tonen

  /** De niveaus in de les, en de tekst en de toets op het niveau van de meeste leerlingen. */
  const groepsles = les.vorm === 'dakpan';
  const haalToets = (lesId, n) => (groepsles ? Promise.resolve(null) : api.haalLesToets(lesId, n));

  const laadAlles = async () => {
    setLaden(true);
    try {
      const { niveaus: inLes, voorbeeldniveau } = await api.haalNiveausInLes(les.id);
      setNiveaus(inLes);
      // Na opnieuw maken blijft het gekozen niveau staan.
      const getoond = niveau && inLes.some(n => n.niveau === niveau) ? niveau : voorbeeldniveau;
      setNiveau(getoond);
      const [tekstData, toetsData] = await Promise.all([api.haalLesTekst(les.id, getoond), haalToets(les.id, getoond)]);
      setTekst(tekstData);
      setToets(toetsData);
    } catch (err) {
      meldFout(err.message);
    } finally {
      setLaden(false);
    }
  };

  useEffect(() => {
    laadAlles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, [les.id]);

  const kiesNiveau = async (nieuw) => {
    setNiveau(nieuw);
    setLaden(true);
    try {
      const [tekstData, toetsData] = await Promise.all([api.haalLesTekst(les.id, nieuw), haalToets(les.id, nieuw)]);
      setTekst(tekstData);
      setToets(toetsData);
    } catch (err) {
      meldFout(err.message);
    } finally {
      setLaden(false);
    }
  };

  const maakOpnieuw = async () => {
    if (!window.confirm(`Weet je zeker dat je de toets op ${niveau} opnieuw wilt laten maken? Dit kan even duren.`)) return;
    setOpnieuw(true);
    setMelding('');
    try {
      const antwoord = await api.maakToetsOpnieuw(les.id, niveau);
      await laadAlles();
      setMelding(antwoord?.message || '');
    } catch (err) {
      meldFout(err.message);
    } finally {
      setOpnieuw(false);
    }
  };

  return (
    <Venster breed={1200} vanBoven>
      <div className="flex-between mb-16">
        <h2>Preview: {les.naam}</h2>
        <div className="flex gap-8">
          {!groepsles && (
            <button className="btn btn-warning" onClick={maakOpnieuw} disabled={opnieuw || laden || !niveau}>
              {opnieuw ? <Draaier /> : <RefreshCw size={16} />}
              {opnieuw ? ' Toets maken...' : ' Toets opnieuw maken'}
            </button>
          )}
          <button className={`btn ${les.is_active ? 'btn-secondary' : 'btn-success'}`} onClick={onAanUit}>
            {les.is_active ? <X size={16} /> : <Check size={16} />}
            {les.is_active ? ' Deactiveren' : ' Activeren voor leerlingen'}
          </button>
          <button className="btn btn-secondary" onClick={onSluit}>Sluiten</button>
        </div>
      </div>

      <div className="card mb-16" style={{ background: 'var(--blauw-vlak)', padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong>Bekijk niveau:</strong>
            <select className="input" style={{ padding: '4px 8px', fontSize: '13px', minWidth: '140px' }}
              value={niveau || ''} onChange={(e) => kiesNiveau(e.target.value)} disabled={laden || opnieuw}>
              {niveaus.map(n => (
                <option key={n.niveau} value={n.niveau}>
                  {n.niveau} - {n.aantal_leerlingen} leerling{n.aantal_leerlingen !== 1 ? 'en' : ''}
                </option>
              ))}
            </select>
          </div>
          <span className="text-muted">|</span>
          {les.leerjaar && <><span>Groep {les.leerjaar}</span><span className="text-muted">|</span></>}
          <span>Status: <span className={`badge ${les.is_active ? 'badge-success' : 'badge-warning'}`}>
            {les.is_active ? 'Actief' : 'Inactief'}
          </span></span>
        </div>
      </div>

      {melding && <div className="alert alert-success mb-16">{melding}</div>}

      {laden ? (
        <Laden tekst="Preview laden..." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <h3 className="mb-8">Tekst</h3>
            {tekst && (
              <>
                <div className="card mb-8" style={{ background: tekst.is_generated ? 'var(--goed-vlak)' : 'var(--waarschuw-vlak)', padding: '8px 12px' }}>
                  <small>
                    {tekst.is_generated ? '✓ Goedgekeurd door de beheerder' : '⚠ Nog niet goedgekeurd: de standaardtekst'} |{' '}
                    Lengte bij dit niveau: {tekst.tekstlengte_instelling || '-'} woorden |{' '}
                    Werkelijk: <strong>{tekst.tekstlengte_werkelijk}</strong> woorden
                  </small>
                  <TekstMaten maten={tekst.maten} niveau={tekst.niveau} />
                  {heeftMarkeringen(tekst.paragraphs) && (
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, fontSize: 13 }}>
                      <input type="checkbox" checked={bouwstenen} onChange={e => setBouwstenen(e.target.checked)} />
                      Bouwstenen tonen
                    </label>
                  )}
                </div>
                <div style={kader}>
                  {tekst.paragraphs.map(p => (
                    <p key={p.id} style={{ marginBottom: '16px', lineHeight: 1.7 }}>
                      {bouwstenen && p.gemarkeerd ? <GemarkeerdeTekst tekst={zonderKopjes(p.gemarkeerd)} /> : zonderKopjes(p.inhoud)}
                    </p>
                  ))}
                </div>
              </>
            )}
          </div>

          {groepsles ? <Groepjes les={les} meldFout={meldFout} /> : <div>
            <h3 className="mb-8">Toets (MC vragen)</h3>
            {toets && (
              <>
                <div className="card mb-8" style={{ background: toets.is_generated ? 'var(--goed-vlak)' : 'var(--waarschuw-vlak)', padding: '8px 12px' }}>
                  <small>
                    {toets.is_generated ? '✓ Gegenereerd' : '⚠ Niet gegenereerd'} |{' '}
                    {toets.mc_vragen.length} MC vragen
                  </small>
                </div>
                <div style={kader}>
                  {toets.mc_vragen.map((vraag, i) => (
                    <div key={i} className="card mb-12" style={{ background: 'var(--vlak)', padding: '12px' }}>
                      <strong>Vraag {i + 1}:</strong> {vraag.vraag}
                      <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
                        {Object.entries(vraag.opties || {}).map(([letter, tekstOptie]) => (
                          <div key={letter} style={{
                            padding: '4px 8px',
                            background: letter === vraag.correct_antwoord ? 'var(--goed-vlak)' : 'var(--wit)',
                            borderRadius: '4px',
                            marginBottom: '4px',
                          }}>
                            <strong>{letter}:</strong> {tekstOptie}
                            {letter === vraag.correct_antwoord && ' ✓'}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>}
        </div>
      )}
    </Venster>
  );
}
